import i18n from "../../i18n";

export type ModalShortcut =
  | { kind: "cancel" }
  | { kind: "default" }
  | { kind: "mnemonic"; letter: string };

export interface ModalKeyEvent {
  key: string;
  code?: string;
  altKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
  isComposing: boolean;
  defaultPrevented: boolean;
}

export interface ModalFocusInfo {
  tagName: string;
  isContentEditable: boolean;
  ariaExpanded: string | null;
  role: string | null;
}

// Note: Enter already does something useful in these, so the default action must keep out of the way:
// a focused button/link activates itself, a textarea takes a newline, a select opens its list.
const SELF_HANDLING_TAGS = new Set(["BUTTON", "A", "SUMMARY", "SELECT", "TEXTAREA"]);

const swallowsEnter = (focus: ModalFocusInfo | null) => {
  if (!focus) return false;
  if (focus.isContentEditable) return true;
  if (SELF_HANDLING_TAGS.has(focus.tagName)) return true;
  // Note: an open combobox popup (MUI Autocomplete, date picker) uses Enter to pick the highlighted option.
  if (focus.ariaExpanded === "true") return true;

  return focus.role === "option" || focus.role === "listbox";
};

/**
 * Physical-key first, so Alt+D resolves to "d" on layouts where the browser reports the
 * composed character instead of the letter.
 */
const mnemonicLetter = (event: ModalKeyEvent): string | null => {
  const fromCode = /^Key([A-Z])$/.exec(event.code ?? "");
  if (fromCode) return fromCode[1].toLowerCase();

  return /^[a-z]$/i.test(event.key) ? event.key.toLowerCase() : null;
};

export const resolveModalShortcut = (
  event: ModalKeyEvent,
  focus: ModalFocusInfo | null
): ModalShortcut | null => {
  if (event.defaultPrevented || event.isComposing) return null;

  if (event.key === "Escape") return { kind: "cancel" };

  if (event.altKey && !event.ctrlKey && !event.metaKey) {
    const letter = mnemonicLetter(event);
    return letter ? { kind: "mnemonic", letter } : null;
  }

  if (event.key === "Enter" && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
    return swallowsEnter(focus) ? null : { kind: "default" };
  }

  return null;
};

export const splitLabelForMnemonic = (label: string, letter: string) => {
  const index = label.toLowerCase().indexOf(letter.toLowerCase());
  if (index < 0) return null;

  return {
    before: label.slice(0, index),
    match: label.slice(index, index + 1),
    after: label.slice(index + 1),
  };
};

export const describeShortcutHint = (mnemonic?: string, hasCancel = true) => {
  const keys = mnemonic ? i18n.t("shortcuts.or", { first: "Enter", second: `Alt+${mnemonic.toUpperCase()}` }) : "Enter";
  const confirm = i18n.t("shortcuts.confirmWith", { keys });

  return hasCancel ? i18n.t("shortcuts.escToCancel", { confirm }) : confirm;
};

export const toModalKeyEvent = (event: KeyboardEvent): ModalKeyEvent => ({
  key: event.key,
  code: event.code,
  altKey: event.altKey,
  ctrlKey: event.ctrlKey,
  metaKey: event.metaKey,
  shiftKey: event.shiftKey,
  isComposing: event.isComposing,
  defaultPrevented: event.defaultPrevented,
});

export const toModalFocusInfo = (element: Element | null): ModalFocusInfo | null =>
  element
    ? {
      tagName: element.tagName,
      isContentEditable: (element as HTMLElement).isContentEditable === true,
      ariaExpanded: element.getAttribute("aria-expanded"),
      role: element.getAttribute("role"),
    }
    : null;

export const DEFAULT_ACTION_ATTR = "data-modal-default";
export const MNEMONIC_ATTR = "data-modal-mnemonic";

export const shortcutSelector = (shortcut: ModalShortcut) =>
  shortcut.kind === "mnemonic"
    ? `[${MNEMONIC_ATTR}="${shortcut.letter}"]`
    : `[${DEFAULT_ACTION_ATTR}="true"]`;
